import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { useBot } from "../hooks/useApi";
import { useTranslation } from "../hooks/useTranslation";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { Skeleton } from "@/components/ui/skeleton";
import { useMinLoadingTime } from "@/hooks/useMinLoadingTime";
import { useToast } from "@/hooks/use-toast";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Brain,
  Target,
  FileQuestion,
  Users,
  MessageSquare,
  TrendingUp,
  AlertCircle,
  Mail,
  Phone,
  ExternalLink,
  Search,
  X,
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import AnalyticsTab from "./AnalyticsTab";
import { BrandCard, BrandStyles, VARIABILI_SEZIONE } from "../components/integrations/brand";
import { GRIGIO, SCURO, IntestazioneCasella } from "../components/integrations/casella";

// ---------------------------------------------------------------------------
// Pezzi di sola grafica: stessa identita' di Panoramica, Conversazioni e Fonti
// ---------------------------------------------------------------------------

// Le voci di un interruttore (schede e filtri), come Interruttore in casella.jsx.
const BINARIO =
  "inline-flex max-w-full items-center overflow-x-auto rounded-[12px] bg-[#F1F5F9] p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";
const VOCE =
  "inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[9px] px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const VOCE_ATTIVA = "bg-white text-[#020617] shadow-[0_1px_2px_rgba(15,23,42,0.08)]";
const VOCE_SPENTA = "text-[#64748B] hover:text-[#020617]";
const SCHEDA = `${VOCE} ${VOCE_SPENTA} py-0 ring-offset-0 focus-visible:ring-offset-0 data-[state=active]:bg-white data-[state=active]:text-[#020617] data-[state=active]:shadow-[0_1px_2px_rgba(15,23,42,0.08)]`;

// Una riga di un elenco dentro un riquadro, e il tondo alla sua sinistra.
const RIGA =
  "flex items-center justify-between gap-3 rounded-[10px] px-3 py-2.5 transition-colors hover:bg-[#F8FAFC]";
const TONDO =
  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F1F5F9] text-xs font-semibold tabular-nums text-[#334155]";
const TONDO_SESSIONE =
  "flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F1F5F9] text-xs font-semibold tabular-nums text-[#334155]";

// Pulsanti con la sola icona: grigio come GRIGIO, o quieto senza fondo.
const ICONA =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";
const ICONA_GRIGIA = `${ICONA} rounded-[10px] bg-[#F1F5F9] text-[#18181B] hover:bg-[#E2E8F0]`;
const ICONA_QUIETA = `${ICONA} rounded-[8px] text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#020617]`;

/** Un riquadro con titolo e descrizione, come i box della Panoramica. */
function Sezione({ Icona, titolo, descrizione, children }) {
  return (
    <BrandCard className="overflow-hidden">
      <div className="px-5 pb-2 pt-4">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-[#020617]">
          {Icona ? <Icona className="h-4 w-4 shrink-0 text-[#64748B]" aria-hidden="true" /> : null}
          {titolo}
        </h2>
        {descrizione ? <p className="mt-0.5 text-xs leading-5 text-[#64748B]">{descrizione}</p> : null}
      </div>
      <div className="px-2 pb-2">{children}</div>
    </BrandCard>
  );
}

/** Riquadro vuoto: icona in un tondo grigio, titolo, una riga e, se serve, il pulsante. */
function Vuoto({ Icona, titolo, testo, azione }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-10 text-center">
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F1F5F9]">
        <Icona className="h-5 w-5 text-[#94A3B8]" aria-hidden="true" />
      </span>
      <div className="max-w-sm space-y-1">
        <p className="text-sm font-medium text-[#020617]">{titolo}</p>
        {testo ? <p className="text-xs leading-5 text-[#64748B]">{testo}</p> : null}
      </div>
      {azione}
    </div>
  );
}

/** Un numero grande con la sua etichetta. */
function Numero({ etichetta, valore }) {
  return (
    <BrandCard className="p-5">
      <span className="block text-xs font-medium text-[#64748B]">{etichetta}</span>
      <span className="mt-2 block text-2xl font-semibold tabular-nums tracking-tight text-[#020617]">{valore}</span>
    </BrandCard>
  );
}

/** Un segno sobrio: un pallino colorato (facoltativo) e una parola. */
function Segno({ colore, className = "", children }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full bg-[#F1F5F9] px-2 py-0.5 text-[11px] font-medium text-[#475569] ${className}`}
    >
      {colore ? <span className={`h-1.5 w-1.5 rounded-full ${colore}`} aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

/** A destra della riga: una barra sottile e la percentuale. */
function Quota({ percentuale }) {
  return (
    <span className="flex shrink-0 items-center gap-3">
      <span className="hidden h-1.5 w-20 overflow-hidden rounded-full bg-[#F1F5F9] sm:block" aria-hidden="true">
        <span className="block h-full rounded-full bg-[#94A3B8]" style={{ width: `${percentuale}%` }} />
      </span>
      <span className="min-w-[3rem] text-right text-xs font-medium tabular-nums text-[#334155]">{percentuale}%</span>
    </span>
  );
}

/** Lo scheletro di un riquadro con un elenco, mentre carica. */
function RiquadroInCaricamento({ righe }) {
  return (
    <BrandCard className="p-5">
      <Skeleton className="h-4 w-40" />
      <Skeleton className="mt-2 h-3 w-64 max-w-full" />
      <div className="mt-4 space-y-1">
        {Array.from({ length: righe }).map((_, i) => (
          <div key={i} className="flex items-center justify-between gap-3 py-2.5">
            <div className="flex min-w-0 items-center gap-3">
              <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
              <div>
                <Skeleton className="mb-1.5 h-3.5 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
            <Skeleton className="h-3 w-12" />
          </div>
        ))}
      </div>
    </BrandCard>
  );
}

// Tab Components
const IntentAnalyticsTab = ({ botId, intelligenceEnabled, onEnable }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ["queryIntelligence", botId],
    queryFn: async () => {
      const response = await api.get(`/analytics/query-intelligence/${botId}`);
      return response; // api.get già restituisce response.data
    },
    refetchInterval: 30000, // Refetch every 30 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    staleTime: 20000, // Consider data stale after 20 seconds
    enabled: intelligenceEnabled, // Only fetch if intelligence is enabled
  });

  const showSkeleton = useMinLoadingTime(isLoading);

  if (showSkeleton) {
    return (
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <BrandCard key={i} className="p-5">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="mt-3 h-8 w-16" />
          </BrandCard>
        ))}
      </div>
    );
  }

  const {
    topIntents = [],
    topContentGaps = [],
    totalQueries = 0,
    avgConfidence = 0,
  } = data || {};

  // Show empty state with CTA if intelligence is disabled
  if (!intelligenceEnabled) {
    return (
      <BrandCard>
        <Vuoto
          Icona={Target}
          titolo={t("intelligence.intentAnalysisDisabled", "Intent Analysis Disabled")}
          testo={t(
            "intelligence.intentAnalysisDisabledDesc",
            "Enable AI Intelligence to automatically track user intents, sentiment, and keywords from conversations."
          )}
          azione={
            <button type="button" onClick={onEnable} className={SCURO}>
              {t("intelligence.enableIntelligenceShort", "Enable Intelligence")}
            </button>
          }
        />
      </BrandCard>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <Numero etichetta={t("intelligence.analytics.totalQueries")} valore={totalQueries} />
      <Numero
        etichetta={t("intelligence.analytics.avgConfidence")}
        valore={<>{(avgConfidence * 100).toFixed(1)}%</>}
      />
      <Numero etichetta={t("intelligence.analytics.intentCategories")} valore={topIntents.length} />
      <Numero etichetta={t("intelligence.analytics.contentGaps")} valore={topContentGaps.length} />
    </div>
  );
};

const IntentAnalysisTab = ({ botId, intelligenceEnabled, onEnable }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ["queryIntelligence", botId],
    queryFn: async () => {
      const response = await api.get(`/analytics/query-intelligence/${botId}`);
      return response; // api.get già restituisce response.data
    },
    refetchInterval: 30000, // Refetch every 30 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    staleTime: 20000, // Consider data stale after 20 seconds
    enabled: intelligenceEnabled, // Only fetch if intelligence is enabled
  });

  const showSkeleton = useMinLoadingTime(isLoading);

  if (showSkeleton) {
    return <RiquadroInCaricamento righe={5} />;
  }

  const {
    topIntents = [],
    topKeywords = [],
    userJourney = [],
    responseQuality = [],
  } = data || {};

  // Show empty state with CTA if intelligence is disabled
  if (!intelligenceEnabled) {
    return (
      <BrandCard>
        <Vuoto
          Icona={Target}
          titolo={t("intelligence.intelligenceDisabled")}
          testo={t("intelligence.intelligenceDisabledDescLong")}
          azione={
            <button type="button" onClick={onEnable} className={SCURO}>
              {t("intelligence.enableIntelligence")}
            </button>
          }
        />
      </BrandCard>
    );
  }

  return (
    <div className="space-y-4">
      {/* Top Intents */}
      <Sezione
        Icona={Target}
        titolo={t("intelligence.intentAnalysis.topIntents")}
        descrizione={t("intelligence.intentAnalysis.topIntentsDesc")}
      >
        {topIntents.length === 0 ? (
          <Vuoto
            Icona={Brain}
            titolo={t("intelligence.intentAnalysis.noIntentData")}
            testo={t("intelligence.intentAnalysis.enableTracking")}
          />
        ) : (
          <ul>
            {topIntents.map((intent, idx) => (
              <li key={idx} className={RIGA}>
                <div className="flex min-w-0 items-center gap-3">
                  <span className={TONDO}>{idx + 1}</span>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium capitalize text-[#020617]">
                      {intent.intent || t("intelligence.unknown", "Unknown")}
                    </div>
                    <div className="text-xs text-[#64748B]">
                      {intent.count}{" "}
                      {intent.count === 1
                        ? t("intelligence.intentAnalysis.query")
                        : t("intelligence.intentAnalysis.queries")}
                    </div>
                  </div>
                </div>
                <Quota percentuale={intent.percentage} />
              </li>
            ))}
          </ul>
        )}
      </Sezione>

      {/* Top Keywords */}
      {topKeywords.length > 0 && (
        <Sezione
          Icona={Search}
          titolo={t("intelligence.intentAnalysis.topKeywords")}
          descrizione={t("intelligence.intentAnalysis.topKeywordsDesc")}
        >
          <div className="flex flex-wrap gap-1.5 px-3 pb-3 pt-1">
            {topKeywords.slice(0, 15).map((keyword, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#E2E8F0] bg-white px-2.5 py-1 text-xs font-medium text-[#334155]"
              >
                {keyword.keyword}
                <span className="tabular-nums text-[#94A3B8]">{keyword.count}</span>
              </span>
            ))}
          </div>
        </Sezione>
      )}

      {/* User Journey & Response Quality */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* User Journey */}
        {userJourney.length > 0 && (
          <Sezione
            Icona={TrendingUp}
            titolo={t("intelligence.intentAnalysis.userJourney")}
            descrizione={t("intelligence.intentAnalysis.userJourneyDesc")}
          >
            <ul>
              {userJourney.map((stage, idx) => (
                <li key={idx} className={RIGA}>
                  <div className="flex min-w-0 items-center gap-3">
                    <span className={TONDO}>{stage.count || stage.percentage}</span>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium capitalize text-[#020617]">
                        {stage.stage}
                      </div>
                      <div className="text-xs text-[#64748B]">
                        {stage.count}{" "}
                        {stage.count === 1
                          ? t("intelligence.intentAnalysis.query")
                          : t("intelligence.intentAnalysis.queries")}
                      </div>
                    </div>
                  </div>
                  <Quota percentuale={stage.percentage} />
                </li>
              ))}
            </ul>
          </Sezione>
        )}

        {/* Response Quality */}
        {responseQuality.length > 0 && (
          <Sezione
            Icona={MessageSquare}
            titolo={t("intelligence.intentAnalysis.responseQuality")}
            descrizione={t("intelligence.intentAnalysis.responseQualityDesc")}
          >
            <ul>
              {responseQuality.map((quality, idx) => (
                <li key={idx} className={RIGA}>
                  <div className="flex min-w-0 items-center gap-3">
                    <span className={TONDO}>
                      <span
                        className={`h-2 w-2 rounded-full ${
                          quality.quality === "complete"
                            ? "bg-emerald-500"
                            : quality.quality === "partial"
                            ? "bg-amber-500"
                            : "bg-red-500"
                        }`}
                        aria-hidden="true"
                      />
                    </span>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium capitalize text-[#020617]">
                        {quality.quality}
                      </div>
                      <div className="text-xs text-[#64748B]">
                        {quality.count}{" "}
                        {quality.count === 1
                          ? t("intelligence.intentAnalysis.response")
                          : t("intelligence.intentAnalysis.responses")}
                      </div>
                    </div>
                  </div>
                  <Quota percentuale={quality.percentage} />
                </li>
              ))}
            </ul>
          </Sezione>
        )}
      </div>
    </div>
  );
};

const ContentGapsTab = ({ botId, intelligenceEnabled, onEnable }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ["queryIntelligence", botId],
    queryFn: async () => {
      const response = await api.get(`/analytics/query-intelligence/${botId}`);
      return response; // api.get già restituisce response.data
    },
    refetchInterval: 30000, // Refetch every 30 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    staleTime: 20000, // Consider data stale after 20 seconds
    enabled: intelligenceEnabled, // Only fetch if intelligence is enabled
  });

  const showSkeleton = useMinLoadingTime(isLoading);

  if (showSkeleton) {
    return <RiquadroInCaricamento righe={5} />;
  }

  const { topContentGaps = [] } = data || {};

  // Show empty state with CTA if intelligence is disabled
  if (!intelligenceEnabled) {
    return (
      <BrandCard>
        <Vuoto
          Icona={FileQuestion}
          titolo={t("intelligence.intelligenceDisabled")}
          testo={t("intelligence.intelligenceDisabledDescLong")}
          azione={
            <button type="button" onClick={onEnable} className={SCURO}>
              {t("intelligence.enableIntelligence")}
            </button>
          }
        />
      </BrandCard>
    );
  }

  return (
    <Sezione
      Icona={FileQuestion}
      titolo={t("intelligence.contentGaps.title")}
      descrizione={t("intelligence.contentGaps.description")}
    >
      {topContentGaps.length === 0 ? (
        <Vuoto
          Icona={FileQuestion}
          titolo={t("intelligence.contentGaps.noGaps")}
          testo={t("intelligence.contentGaps.noGapsDesc")}
        />
      ) : (
        <ul>
          {topContentGaps.map((gap, idx) => {
            // Extract query from suggestions first line: 'Topic: "query text"'
            const queryMatch =
              gap.suggestions?.[0]?.match(/Topic: "(.+?)"/);
            const query = queryMatch
              ? queryMatch[1]
              : gap.keywords?.join(", ") ||
                t("intelligence.unknownQuery", "Unknown query");

            // Extract metrics from suggestions
            const sourcesMatch = gap.suggestions?.[0]?.match(
              /(\d+) found, top score: ([\d.]+)/
            );
            const totalResults = sourcesMatch
              ? parseInt(sourcesMatch[1])
              : null;
            const topScore = sourcesMatch
              ? parseFloat(sourcesMatch[2])
              : null;

            // Get recommendation (last line of suggestions)
            const recommendation =
              gap.suggestions?.[gap.suggestions.length - 1]?.replace(
                "Recommendation: ",
                ""
              ) || null;

            return (
              <li
                key={idx}
                className="flex items-start gap-3 rounded-[10px] px-3 py-3 transition-colors hover:bg-[#F8FAFC]"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#94A3B8]" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <div className="break-words text-sm font-medium text-[#020617]">{query}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-[#64748B]">
                    {totalResults !== null && (
                      <span>
                        {t("intelligence.results", "{{count}} results", {
                          count: totalResults,
                        })}
                      </span>
                    )}
                    {topScore !== null && <span className="tabular-nums">score: {topScore}</span>}
                  </div>
                  {recommendation && (
                    <div className="mt-2 rounded-[8px] bg-[#F8FAFC] px-2.5 py-1.5 text-xs leading-5 text-[#475569]">
                      {recommendation}
                    </div>
                  )}
                </div>
                <button type="button" className={ICONA_QUIETA}>
                  <ExternalLink className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Sezione>
  );
};

const SessionsTab = ({ botId, intelligenceEnabled, onEnable }) => {
  const { t } = useTranslation();
  const [selectedSession, setSelectedSession] = React.useState(null);
  const [filter, setFilter] = React.useState("all"); // all, hot, contact

  const { data, isLoading } = useQuery({
    queryKey: ["recentIntelligence", botId],
    queryFn: async () => {
      const response = await api.get(
        `/analytics/query-intelligence/${botId}/recent?limit=100`
      );
      return response; // api.get già restituisce response.data
    },
    refetchInterval: 15000, // Refetch every 15 seconds (more frequent for leads)
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    staleTime: 10000, // Consider data stale after 10 seconds
    enabled: intelligenceEnabled, // Only fetch if intelligence is enabled
  });

  const showSkeleton = useMinLoadingTime(isLoading);

  if (showSkeleton) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-80 max-w-full rounded-[12px]" />
        <BrandCard className="p-5">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="mt-2 h-3 w-64 max-w-full" />
          <div className="mt-4 space-y-1">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-start gap-3 py-3">
                <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
                <div className="min-w-0 flex-1">
                  <Skeleton className="mb-2 h-3.5 w-40" />
                  <Skeleton className="mb-2 h-3 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </BrandCard>
      </div>
    );
  }

  const { intelligence = [] } = data || {};

  // Show empty state with CTA if intelligence is disabled
  if (!intelligenceEnabled) {
    return (
      <BrandCard>
        <Vuoto
          Icona={Users}
          titolo={t("intelligence.intelligenceDisabled")}
          testo={t("intelligence.intelligenceDisabledDescLong")}
          azione={
            <button type="button" onClick={onEnable} className={SCURO}>
              {t("intelligence.enableIntelligence")}
            </button>
          }
        />
      </BrandCard>
    );
  }

  // Group by session
  const sessionMap = {};
  intelligence.forEach((item) => {
    const sid = item.sessionId;
    if (!sessionMap[sid]) {
      sessionMap[sid] = {
        sessionId: sid,
        queries: [],
        maxLeadScore: 0,
        latestTimestamp: item.timestamp,
        contactInfo: null,
        hasContentGap: false,
      };
    }
    sessionMap[sid].queries.push(item);
    sessionMap[sid].maxLeadScore = Math.max(
      sessionMap[sid].maxLeadScore,
      item.leadScore || 0
    );
    if (new Date(item.timestamp) > new Date(sessionMap[sid].latestTimestamp)) {
      sessionMap[sid].latestTimestamp = item.timestamp;
    }
    // Extract contact info
    if (
      item.contactInfo &&
      (item.contactInfo.email || item.contactInfo.phone)
    ) {
      sessionMap[sid].contactInfo = item.contactInfo;
    }
    // Check for content gap
    if (item.hasContentGap) {
      sessionMap[sid].hasContentGap = true;
    }
  });

  const allSessions = Object.values(sessionMap).sort(
    (a, b) => new Date(b.latestTimestamp) - new Date(a.latestTimestamp)
  );

  // Apply filter
  const sessions = allSessions.filter((session) => {
    if (filter === "hot") return session.maxLeadScore >= 60;
    if (filter === "contact") return session.contactInfo !== null;
    return true;
  });

  const hotLeadsCount = allSessions.filter((s) => s.maxLeadScore >= 60).length;
  const withContactCount = allSessions.filter(
    (s) => s.contactInfo !== null
  ).length;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className={BINARIO}>
        <button
          type="button"
          aria-pressed={filter === "all"}
          onClick={() => setFilter("all")}
          className={`${VOCE} ${filter === "all" ? VOCE_ATTIVA : VOCE_SPENTA}`}
        >
          {t("intelligence.sessions.allSessions")}
          <span className={`text-xs tabular-nums ${filter === "all" ? "text-[#64748B]" : "text-[#94A3B8]"}`}>
            {allSessions.length}
          </span>
        </button>
        <button
          type="button"
          aria-pressed={filter === "hot"}
          onClick={() => setFilter("hot")}
          className={`${VOCE} ${filter === "hot" ? VOCE_ATTIVA : VOCE_SPENTA}`}
        >
          {t("intelligence.sessions.hotLeads")}
          <span className={`text-xs tabular-nums ${filter === "hot" ? "text-[#64748B]" : "text-[#94A3B8]"}`}>
            {hotLeadsCount}
          </span>
        </button>
        <button
          type="button"
          aria-pressed={filter === "contact"}
          onClick={() => setFilter("contact")}
          className={`${VOCE} ${filter === "contact" ? VOCE_ATTIVA : VOCE_SPENTA}`}
        >
          {t("intelligence.sessions.withContact")}
          <span className={`text-xs tabular-nums ${filter === "contact" ? "text-[#64748B]" : "text-[#94A3B8]"}`}>
            {withContactCount}
          </span>
        </button>
      </div>

      <Sezione
        Icona={Users}
        titolo={
          <>
            {filter === "all" && t("intelligence.sessions.recentSessions")}
            {filter === "hot" && t("intelligence.sessions.hotLeadSessions")}
            {filter === "contact" &&
              t("intelligence.sessions.sessionsWithContact")}
          </>
        }
        descrizione={
          <>
            {filter === "all" && t("intelligence.sessions.recentSessionsDesc")}
            {filter === "hot" && t("intelligence.sessions.hotLeadSessionsDesc")}
            {filter === "contact" &&
              t("intelligence.sessions.sessionsWithContactDesc")}
          </>
        }
      >
        {sessions.length === 0 ? (
          <Vuoto Icona={Users} titolo={t("intelligence.sessions.noSessions")} />
        ) : (
          <ul>
            {sessions.map((session) => {
              const topIntent = session.queries[0]?.intent || "general";
              const firstQuery = session.queries[0]?.query || "";

              return (
                <li
                  key={session.sessionId}
                  className="flex cursor-pointer items-start justify-between gap-3 rounded-[10px] px-3 py-3 transition-colors hover:bg-[#F8FAFC]"
                  onClick={() => setSelectedSession(session)}
                >
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    {/* Icon */}
                    <span className={TONDO_SESSIONE}>{session.queries.length}</span>

                    {/* Info */}
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="mr-0.5 text-sm font-medium text-[#020617]">
                          {t("intelligence.sessions.sessionLabel", "Session")}{" "}
                          {session.sessionId.substring(0, 8)}
                        </span>
                        {session.maxLeadScore >= 60 && (
                          <Segno colore="bg-[#F97316]">
                            {t("intelligence.sessions.hotLeadBadge", "Hot lead")}
                          </Segno>
                        )}
                        {session.contactInfo && (
                          <Segno colore="bg-[#A855F7]">
                            {t("intelligence.sessions.contactBadge", "Contact")}
                          </Segno>
                        )}
                        {session.hasContentGap && (
                          <Segno colore="bg-[#94A3B8]">
                            {t("intelligence.sessions.contentGapBadge", "Content gap")}
                          </Segno>
                        )}
                        <Segno className="capitalize">{topIntent}</Segno>
                      </div>

                      {/* First query preview */}
                      <p className="truncate text-[13px] text-[#64748B]">
                        "{firstQuery.substring(0, 80)}
                        {firstQuery.length > 80 && "..."}"
                      </p>

                      {/* Contact info */}
                      {session.contactInfo && (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          {session.contactInfo.email && (
                            <div className="flex min-w-0 items-center gap-1 text-xs text-[#475569]">
                              <Mail className="h-3.5 w-3.5 shrink-0 text-[#94A3B8]" />
                              <span className="truncate">
                                {session.contactInfo.email}
                              </span>
                            </div>
                          )}
                          {session.contactInfo.phone && (
                            <div className="flex items-center gap-1 text-xs text-[#475569]">
                              <Phone className="h-3.5 w-3.5 shrink-0 text-[#94A3B8]" />
                              <span className="tabular-nums">
                                {session.contactInfo.phone}
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Stats */}
                      <div className="text-xs text-[#94A3B8]">
                        {session.queries.length}{" "}
                        {t("intelligence.sessions.interactions")} •{" "}
                        {t("intelligence.sessions.score")}:{" "}
                        {session.maxLeadScore} •{" "}
                        {new Date(
                          session.latestTimestamp
                        ).toLocaleDateString()}{" "}
                        {new Date(
                          session.latestTimestamp
                        ).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  {session.contactInfo && (
                    <div className="flex shrink-0 items-center gap-1.5">
                      {session.contactInfo.email && (
                        <button
                          type="button"
                          className={ICONA_GRIGIA}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(
                              session.contactInfo.email
                            );
                          }}
                          title={t("intelligence.sessions.copyEmail", "Copy email")}
                        >
                          <Mail className="h-4 w-4" />
                        </button>
                      )}
                      {session.contactInfo.phone && (
                        <button
                          type="button"
                          className={ICONA_GRIGIA}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = `tel:${session.contactInfo.phone}`;
                          }}
                          title={t("intelligence.sessions.call", "Call")}
                        >
                          <Phone className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Sezione>

      {/* Session Detail Modal: nel body, perche' la pagina (animata e
          isolata) non lo tenga dentro di se' e sotto la barra in alto. */}
      {selectedSession && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#020617]/40 p-4"
          style={VARIABILI_SEZIONE}
          onClick={() => setSelectedSession(null)}
        >
          <BrandCard
            className="w-full max-w-4xl max-h-[80vh] overflow-hidden shadow-[0_20px_50px_rgba(15,23,42,0.18)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 border-b border-[#E2E8F0] px-5 py-4">
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-[#020617]">
                  {t("intelligence.sessions.sessionDetails")}
                </h2>
                <p className="mt-0.5 text-xs text-[#64748B]">
                  {selectedSession.queries.length}{" "}
                  {t("intelligence.sessions.interactions")} •{" "}
                  {t("intelligence.sessions.leadScore")}:{" "}
                  {selectedSession.maxLeadScore}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedSession(null)}
                aria-label={t("common.close", "Chiudi")}
                className={ICONA_QUIETA}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-5">
              <div className="space-y-3">
                {selectedSession.queries.map((query, idx) => (
                  <div key={idx} className="rounded-[12px] border border-[#E2E8F0] p-4">
                    {/* Query */}
                    <div className="mb-3">
                      <div className="mb-2 flex flex-wrap items-center gap-1.5">
                        <Segno>
                          {t("intelligence.sessions.query")} #{idx + 1}
                        </Segno>
                        <Segno className="capitalize">{query.intent}</Segno>
                        <Segno
                          colore={
                            query.leadScore >= 60
                              ? "bg-[#F97316]"
                              : "bg-[#94A3B8]"
                          }
                        >
                          {t("intelligence.sessions.score")}:{" "}
                          {query.leadScore}
                        </Segno>
                        {query.hasContentGap && (
                          <Segno colore="bg-[#94A3B8]">
                            {t("intelligence.sessions.contentGap")}
                          </Segno>
                        )}
                      </div>
                      <p className="text-sm font-medium text-[#020617]">{query.query}</p>
                    </div>

                    {/* Answer */}
                    <div className="mb-2 rounded-[10px] bg-[#F8FAFC] p-3">
                      <p className="text-[13px] leading-6 text-[#475569]">
                        {query.answer?.substring(0, 300)}
                        {query.answer?.length > 300 && "..."}
                      </p>
                    </div>

                    {/* Metadata */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#94A3B8]">
                      <span>
                        {new Date(query.timestamp).toLocaleString()}
                      </span>
                      {query.sentiment?.tone && (
                        <Segno className="capitalize">
                          {query.sentiment.tone}
                        </Segno>
                      )}
                      {query.answerQuality && (
                        <Segno className="capitalize">
                          {query.answerQuality}
                        </Segno>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </BrandCard>
        </div>,
        document.body
      )}
    </div>
  );
};

// Main Component
const Intelligence = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("analytics");
  const [isToggling, setIsToggling] = useState(false);
  const { data: bot, isLoading } = useBot(id);
  const showSkeleton = useMinLoadingTime(isLoading);

  const handleEnableIntelligence = () => {
    // Navigate to billing page with query param to highlight intelligence
    navigate("/billing?highlight=intelligence");
  };

  if (showSkeleton) {
    return (
      <div className="mx-auto max-w-[1400px] space-y-4 pt-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-96 max-w-full rounded-[12px]" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-[14px]" />
          ))}
        </div>
      </div>
    );
  }

  if (!bot) {
    return (
      <BrandCard className="mx-auto mt-6 max-w-lg p-6 text-center">
        <Brain className="mx-auto h-8 w-8 text-[#94A3B8]" aria-hidden="true" />
        <p className="mt-3 text-sm text-[#64748B]">
          {t("intelligence.botNotFound", "Bot not found")}
        </p>
      </BrandCard>
    );
  }

  const intelligenceEnabled = bot.intelligence_enabled || false;

  // Toggle intelligence
  const toggleIntelligence = async () => {
    setIsToggling(true);
    try {
      await api.patch(`/bots/${id}`, {
        intelligence_enabled: !intelligenceEnabled,
      });
      // Invalidate bot query to trigger refetch with new state
      await queryClient.invalidateQueries(["bots", id]);
    } catch (error) {
      console.error("Failed to toggle intelligence:", error);
      toast({
        variant: "destructive",
        title: t(
          "intelligence.toggleFailed",
          "Failed to update intelligence setting. Please try again."
        ),
      });
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <div className="relative isolate" style={VARIABILI_SEZIONE}>
      <BrandStyles />

      <IntestazioneCasella
        icona={Brain}
        titolo={t("intelligence.title", "Analytics")}
        dettagli={
          intelligenceEnabled ? (
            <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#19D163]/10 px-2.5 py-0.5 text-xs font-medium text-[#0F7A3D]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#19D163]" aria-hidden="true" />
                {t("intelligence.trackingActive")}
              </span>
              <span className="max-w-xl text-sm text-[#64748B]">
                {t("intelligence.trackingActiveDesc")}
              </span>
            </span>
          ) : (
            <span className="line-clamp-1 max-w-xl text-sm text-[#64748B]">
              {t(
                "intelligence.description",
                "AI-powered insights into user queries and content gaps"
              )}
            </span>
          )
        }
        azioni={
          intelligenceEnabled ? (
            <button
              type="button"
              onClick={toggleIntelligence}
              disabled={isToggling}
              className={GRIGIO}
            >
              {isToggling
                ? t("intelligence.disabling")
                : t("intelligence.disable")}
            </button>
          ) : null
        }
      />

      <div className="pu-fade-up mx-auto max-w-[1400px] space-y-6 pb-10">
        {/* Intelligence Status: spenta, cosa si ottiene accendendola */}
        {!intelligenceEnabled ? (
          <BrandCard className="grid gap-6 p-5 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start sm:p-6">
            <div className="max-w-2xl">
              <h2 className="flex flex-wrap items-center gap-2 text-base font-semibold text-[#020617]">
                {t("intelligence.unlockTitle", "Unlock AI Intelligence")}
                <span className="rounded-full bg-[#F1F5F9] px-2 py-0.5 text-[11px] font-medium text-[#475569]">
                  {t("intelligence.messagePerQuery", "+1 message/query")}
                </span>
              </h2>
              <p className="mt-1 text-sm leading-6 text-[#475569]">
                {t(
                  "intelligence.unlockDesc",
                  "Enable intelligent conversation analysis to automatically track:"
                )}
              </p>
              <ul className="mt-3 space-y-2 text-sm text-[#475569]">
                <li className="flex items-start gap-2.5">
                  <Target className="mt-0.5 h-4 w-4 shrink-0 text-[#64748B]" aria-hidden="true" />
                  <span>
                    <strong className="font-medium text-[#020617]">
                      {t("intelligence.unlockIntents", "Query intents")}
                    </strong>{" "}
                    {t(
                      "intelligence.unlockIntentsSuffix",
                      "and sentiment analysis"
                    )}
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Users className="mt-0.5 h-4 w-4 shrink-0 text-[#64748B]" aria-hidden="true" />
                  <span>
                    <strong className="font-medium text-[#020617]">
                      {t("intelligence.unlockLeads", "Qualified leads")}
                    </strong>{" "}
                    {t(
                      "intelligence.unlockLeadsSuffix",
                      "with commercial signals"
                    )}
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <FileQuestion className="mt-0.5 h-4 w-4 shrink-0 text-[#64748B]" aria-hidden="true" />
                  <span>
                    <strong className="font-medium text-[#020617]">
                      {t("intelligence.unlockGaps", "Content gaps")}
                    </strong>{" "}
                    {t("intelligence.unlockGapsSuffix", "to improve your KB")}
                  </span>
                </li>
              </ul>
            </div>
            <div className="flex flex-col items-start gap-2 sm:items-end">
              <button
                type="button"
                onClick={toggleIntelligence}
                disabled={isToggling}
                className={`${SCURO} whitespace-nowrap`}
              >
                {isToggling
                  ? t("intelligence.enabling", "Enabling...")
                  : t("intelligence.enableIntelligenceShort", "Enable Intelligence")}
              </button>
              <p className="text-xs text-[#64748B]">
                {t("intelligence.unlockCost", "Cost: +1 message per query")}
              </p>
            </div>
          </BrandCard>
        ) : null}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={`${BINARIO} h-auto justify-start text-[#64748B]`}>
            <TabsTrigger value="analytics" className={SCHEDA}>
              {t("intelligence.tabs.analytics")}
            </TabsTrigger>
            <TabsTrigger value="intents" className={SCHEDA}>
              {t("intelligence.tabs.intentAnalysis")}
            </TabsTrigger>
            <TabsTrigger value="gaps" className={SCHEDA}>
              {t("intelligence.tabs.contentGaps")}
            </TabsTrigger>
            <TabsTrigger value="sessions" className={SCHEDA}>
              {t("intelligence.tabs.sessions")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="analytics" className="mt-4">
            <AnalyticsTab botId={id} />
          </TabsContent>

          <TabsContent value="intents" className="mt-4">
            <IntentAnalysisTab
              botId={id}
              intelligenceEnabled={intelligenceEnabled}
              onEnable={toggleIntelligence}
            />
          </TabsContent>

          <TabsContent value="gaps" className="mt-4">
            <ContentGapsTab
              botId={id}
              intelligenceEnabled={intelligenceEnabled}
              onEnable={toggleIntelligence}
            />
          </TabsContent>

          <TabsContent value="sessions" className="mt-4">
            <SessionsTab
              botId={id}
              intelligenceEnabled={intelligenceEnabled}
              onEnable={toggleIntelligence}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Intelligence;
